import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { LLMSettings, DEFAULT_LLM_SETTINGS } from '../types';

// ─── Styled Components ────────────────────────────────────────────────────────

const GearButton = styled.button<{ accentColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 2px solid ${props => props.accentColor}40;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.accentColor}10;
    border-color: ${props => props.accentColor};
  }

  &:focus-visible {
    outline: 2px solid ${props => props.accentColor};
    outline-offset: 2px;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: rotate(60deg);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
`;

const Panel = styled.div<{ borderColor: string }>`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 340px;
  background: white;
  border: 1px solid ${props => props.borderColor};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 20px;
`;

const PanelHeader = styled.div<{ borderColor: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${props => props.borderColor};
`;

const PanelTitle = styled.h3<{ color: string }>`
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.color};
  margin: 0;
  flex: 1;
`;

const ResetLink = styled.button<{ color: string }>`
  background: none;
  border: none;
  color: ${props => props.color};
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const SettingRow = styled.div`
  margin-bottom: 16px;
`;

const SettingLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const LabelText = styled.span<{ color: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.color};
`;

const ValueBadge = styled.span<{ bgColor: string; textColor: string }>`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  min-width: 44px;
  text-align: center;
`;

const Slider = styled.input<{ accentColor: string }>`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  appearance: none;
  background: linear-gradient(
    to right,
    ${props => props.accentColor} 0%,
    ${props => props.accentColor} var(--pct, 50%),
    #e1e8ed var(--pct, 50%)
  );
  cursor: pointer;
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.accentColor};
    border: 2px solid white;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
    cursor: pointer;
    transition: transform 0.15s;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }
`;

const HintText = styled.p<{ color: string }>`
  font-size: 11px;
  color: ${props => props.color};
  margin: 4px 0 0;
  line-height: 1.4;
`;

const ApplyButton = styled.button<{ bgColor: string }>`
  width: 100%;
  padding: 10px;
  background: ${props => props.bgColor};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const SegmentGroup = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
`;

const SegmentButton = styled.button<{ active: boolean; accentColor: string }>`
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid ${props => props.active ? props.accentColor : '#e1e8ed'};
  border-radius: 6px;
  background: ${props => props.active ? `${props.accentColor}12` : 'white'};
  color: ${props => props.active ? props.accentColor : '#6b7280'};
  font-size: 13px;
  font-weight: ${props => props.active ? 600 : 400};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${props => props.accentColor};
  }
`;

// ─── Props ────────────────────────────────────────────────────────────────────

interface LLMSettingsPanelProps {
  settings: LLMSettings;
  onChange: (settings: LLMSettings) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LLMSettingsPanel: React.FC<LLMSettingsPanelProps> = ({ settings, onChange }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<LLMSettings>(settings);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setDraft(settings); }, [settings]);

  const handleOpen = () => {
    setDraft(settings);
    setOpen(true);
  };

  const handleApply = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleReset = () => {
    setDraft({ ...DEFAULT_LLM_SETTINGS });
  };

  const sliderPct = (value: number, min: number, max: number) =>
    ((value - min) / (max - min)) * 100;

  return (
    <Container ref={panelRef}>
      <GearButton
        accentColor={theme.colors.accentTeal}
        onClick={handleOpen}
        title="LLM Settings"
        aria-label="Open LLM settings"
        aria-expanded={open}
        type="button"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accentTeal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
        </svg>
      </GearButton>

      {open && (
        <>
          <Overlay onClick={() => setOpen(false)} />
          <Panel borderColor={theme.colors.border}>
            <PanelHeader borderColor={theme.colors.border}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accentTeal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
              </svg>
              <PanelTitle color={theme.colors.textPrimary}>LLM Settings</PanelTitle>
              <ResetLink color={theme.colors.accentTeal} onClick={handleReset} type="button">
                Reset
              </ResetLink>
            </PanelHeader>

            {/* Reasoning Effort */}
            <SettingRow>
              <SettingLabel>
                <LabelText color={theme.colors.textPrimary}>Reasoning Effort</LabelText>
              </SettingLabel>
              <SegmentGroup>
                {(['low', 'medium', 'high'] as const).map(level => (
                  <SegmentButton
                    key={level}
                    type="button"
                    active={draft.reasoning_effort === level}
                    accentColor={theme.colors.accentTeal}
                    onClick={() => setDraft(prev => ({ ...prev, reasoning_effort: level }))}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </SegmentButton>
                ))}
              </SegmentGroup>
              <HintText color={theme.colors.textMuted}>
                Controls how deeply the model reasons. High = more thorough but slower; Low = faster, less detailed.
              </HintText>
            </SettingRow>

            {/* Max Tokens */}
            <SettingRow>
              <SettingLabel>
                <LabelText color={theme.colors.textPrimary}>Max Tokens</LabelText>
                <ValueBadge bgColor={`${theme.colors.success}15`} textColor={theme.colors.success}>
                  {draft.max_tokens}
                </ValueBadge>
              </SettingLabel>
              <Slider
                type="range" min={512} max={16384} step={256}
                value={draft.max_tokens}
                accentColor={theme.colors.success}
                style={{ '--pct': `${sliderPct(draft.max_tokens, 512, 16384)}%` } as React.CSSProperties}
                onChange={e => setDraft(prev => ({ ...prev, max_tokens: parseInt(e.target.value, 10) }))}
              />
              <HintText color={theme.colors.textMuted}>
                Maximum tokens in the response. Increase for large mapping jobs.
              </HintText>
            </SettingRow>

            {/* Max Lines to Read */}
            <SettingRow>
              <SettingLabel>
                <LabelText color={theme.colors.textPrimary}>Max Lines to Read</LabelText>
                <ValueBadge bgColor={`${theme.colors.optumBlue}10`} textColor={theme.colors.optumBlue}>
                  {draft.max_lines}
                </ValueBadge>
              </SettingLabel>
              <Slider
                type="range" min={5} max={100} step={5}
                value={draft.max_lines}
                accentColor={theme.colors.optumBlue}
                style={{ '--pct': `${sliderPct(draft.max_lines, 5, 100)}%` } as React.CSSProperties}
                onChange={e => setDraft(prev => ({ ...prev, max_lines: parseInt(e.target.value, 10) }))}
              />
              <HintText color={theme.colors.textMuted}>
                Number of lines read from your uploaded file for mapping. Larger files with many claims only need the first few records for field detection.
              </HintText>
            </SettingRow>

            <ApplyButton bgColor={theme.colors.accentTeal} onClick={handleApply} type="button">
              Apply Settings
            </ApplyButton>
          </Panel>
        </>
      )}
    </Container>
  );
};
